import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { MapIcon, SearchXIcon, SlidersHorizontalIcon } from 'lucide-react';
import { AppShell } from '../../components/layout/AppShell';
import { FilterPanel } from '../../components/discovery/FilterPanel';
import { ResultsMap } from '../../components/discovery/ResultsMap';
import { ChefCard } from '../../components/chefs/ChefCard';
import { FeaturedChefCard } from '../../components/chefs/FeaturedChefCard';
import { Button } from '../../components/ui/Button';
import { RemovableChip } from '../../components/ui/Chip';
import { Sheet } from '../../components/ui/Sheet';
import { EmptyState } from '../../components/ui/Panels';
import { Select } from '../../components/ui/Field';
import { chefs } from '../../data/chefs';
import { cuisineLabel } from '../../data/taxonomy';
import { customer } from '../../data/customer';
import { activeFilterCount, defaultFilters, searchChefs } from '../../utils/search';
import type { Filters, SortKey } from '../../utils/search';
import { popItem, staggerContainer } from '../../utils/motion';
export function Discover() {
  const [params] = useSearchParams();
  const reduce = useReducedMotion();
  const [filters, setFilters] = useState<Filters>(() => ({
    ...defaultFilters,
    where: params.get('where') ?? defaultFilters.where,
    when: params.get('when') ?? '',
    guests: Number(params.get('guests')) || defaultFilters.guests,
    fulfillment: params.get('fulfillment') as Filters['fulfillment'] ?? 'any',
    cuisines: params.get('cuisine') ? [params.get('cuisine') as string] : []
  }));
  const [sort, setSort] = useState<SortKey>('recommended');
  const [hoveredId, setHoveredId] = useState<string | undefined>();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const origin = {
    lat: customer.lat,
    lng: customer.lng
  };
  const results = useMemo(() => searchChefs(chefs, filters, origin, sort), [filters, origin.lat, origin.lng, sort]);
  const count = activeFilterCount(filters);
  const [top, ...others] = results;
  const removeCuisine = (id: string) => setFilters({
    ...filters,
    cuisines: filters.cuisines.filter(c => c !== id)
  });
  return <AppShell title="Find a chef" subtitle={`${results.length} chefs near ${filters.where} · ${filters.guests} guests`} actions={<>
          <Button variant="outline" size="sm" className="lg:hidden" onClick={() => setFiltersOpen(true)} data-id="element-1907" data-mp="1g63iu2c68a80">
            <SlidersHorizontalIcon className="h-4 w-4" aria-hidden="true" data-id="element-1908" data-mp="1b68sq107zoo0" />
            Filters{count ? ` (${count})` : ''}
          </Button>
          <Button variant="outline" size="sm" className="xl:hidden" onClick={() => setMapOpen(true)} data-id="element-1909" data-mp="1698o7uj0qtz0">
            <MapIcon className="h-4 w-4" aria-hidden="true" data-id="element-1910" data-mp="35k0chtc9jox6" />
            Map
          </Button>
        </>} data-id="element-1906" data-mp="1p5qnqy43zy5t">
      <div className="grid gap-6 xl:grid-cols-[240px_minmax(0,1fr)_300px]" data-id="element-1911" data-mp="30jm60fqb8n19">
        <aside className="hidden xl:block" data-id="element-1912" data-mp="384oixfe4upu3">
          <div className="sticky top-24 rounded-xl border border-hairline bg-surface p-4" data-id="element-1913" data-mp="3d416618x1yx7">
            <FilterPanel filters={filters} onChange={setFilters} onReset={() => setFilters(defaultFilters)} data-id="element-1914" data-mp="3hv10cyqni2w0" />
          </div>
        </aside>

        <div className="min-w-0" data-id="element-1915" data-mp="3mt2o3wz0uqdt">
          <div className="mb-4 flex flex-wrap items-center gap-2" data-id="element-1916" data-mp="3mw6vf37sa28p">
            <AnimatePresence initial={false} data-id="element-1917" data-mp="2p7ncrr8uejok">
              {filters.cuisines.map(id => <motion.span key={id} layout initial={reduce ? {
              opacity: 0
            } : {
              opacity: 0,
              scale: 0.94
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={reduce ? {
              opacity: 0
            } : {
              opacity: 0,
              scale: 0.94
            }} transition={{
              type: 'spring',
              stiffness: 500,
              damping: 30
            }} data-id="element-1918" data-mp="2u6hx4xmgkgas">
                  <RemovableChip onRemove={() => removeCuisine(id)} data-id="element-1919" data-mp="jccb2y50vd2m">{cuisineLabel(id)}</RemovableChip>
                </motion.span>)}
            </AnimatePresence>
            <label className="ml-auto flex items-center gap-2 text-[13px] text-ink-2" data-id="element-1920" data-mp="cxn7nqeuvnea">
              Sort
              <Select value={sort} onChange={e => setSort(e.target.value as SortKey)} className="w-auto py-1.5 text-[13px]" data-id="element-1921" data-mp="qxfxljik5zl7">
                <option value="recommended" data-id="element-1922" data-mp="lx1yws2uk1zu">Recommended</option>
                <option value="price_asc" data-id="element-1923" data-mp="zx2wnyntetb3">Price, low to high</option>
                <option value="rating" data-id="element-1924" data-mp="zv8a6fwwat4w">Rating</option>
                <option value="response" data-id="element-1925" data-mp="vuy8vgk1sqwd">Fastest to reply</option>
              </Select>
            </label>
          </div>

          {results.length === 0 ? <EmptyState icon={<SearchXIcon className="h-5 w-5" aria-hidden="true" data-id="element-1927" data-mp="14pcjh5tmvd95" />} title="Nothing matches all of that" body="Try widening the distance or lifting the price ceiling — those two rule out the most chefs." action={<Button variant="primary" onClick={() => setFilters(defaultFilters)} data-id="element-1928" data-mp="1eko23z85x4m6">
                  Reset filters
                </Button>} data-id="element-1926" data-mp="14obar803b2ul" /> : <>
              <div onMouseEnter={() => setHoveredId(top.chef.id)} onMouseLeave={() => setHoveredId(undefined)} data-id="element-1929" data-mp="1oa3ckmixt859">
                <FeaturedChefCard chef={top.chef} reason={sort === 'price_asc' ? 'Lowest per-guest price that fits your party' : `${top.distance} km away · replies fastest in this list`} data-id="element-1930" data-mp="1t8xt1htfmkm7" />
              </div>

              <motion.ul variants={staggerContainer(0.03, 0.02)} initial="hidden" animate="show" className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-1931" data-mp="15l6fyc02i3jv">
                {others.slice(0, 8).map(({
              chef
            }) => <motion.li key={chef.id} variants={popItem(reduce)} onMouseEnter={() => setHoveredId(chef.id)} onMouseLeave={() => setHoveredId(undefined)} data-id="element-1932" data-mp="10jh1snhjt9rk">
                    <ChefCard chef={chef} data-id="element-1933" data-mp="7q3tlvs8fa86" />
                  </motion.li>)}
              </motion.ul>

              <p className="mt-6 text-center text-[13px] text-ink-3" data-id="element-1934" data-mp="2rj4osifkcz1">
                Showing {Math.min(results.length, 9)} of {results.length} chefs who can cover {filters.guests} guests.
              </p>
            </>}
        </div>

        <aside className="hidden xl:block" data-id="element-1935" data-mp="vg7zk9sw7m2c">
          <ResultsMap results={results} origin={origin} hoveredId={hoveredId} onHover={setHoveredId} className="sticky top-24 h-[calc(100vh-8rem)]" data-id="element-1936" data-mp="qgbt83wq3ijm" />
        </aside>
      </div>

      <Sheet open={filtersOpen} onClose={() => setFiltersOpen(false)} title="Filters" description="Narrow the list" side="bottom" data-id="element-1937" data-mp="3b8dxtg9feizx">
        <FilterPanel filters={filters} onChange={setFilters} onReset={() => setFilters(defaultFilters)} data-id="element-1938" data-mp="36a2l8xrqxue0" />
        <Button variant="action" full className="mt-4" onClick={() => setFiltersOpen(false)} data-id="element-1939" data-mp="1qorzkrp1byrt">
          Show {results.length} chefs
        </Button>
      </Sheet>

      <Sheet open={mapOpen} onClose={() => setMapOpen(false)} title="Chefs near you" side="bottom" data-id="element-1940" data-mp="1vmacsua3505m">
        <ResultsMap results={results} origin={origin} hoveredId={hoveredId} onHover={setHoveredId} className="h-[60vh]" data-id="element-1941" data-mp="20g681oyys8x3" />
      </Sheet>
    </AppShell>;
}